import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { Helmet } from "react-helmet-async";
import Hero from "../components/Hero";

// Aggressive Lazy Loading: Only download these when the user scrolls near them
const Features = lazy(() => import("../components/Features"));
const About = lazy(() => import("./About"));
const Programs = lazy(() => import("./Programs"));
const Contact = lazy(() => import("./Contact"));

const SectionSkeleton = ({ minHeight }) => (
  <div
    style={{ minHeight }}
    className="w-full bg-[#f5f5f5] flex items-center justify-center"
  >
    <div className="max-w-7xl mx-auto w-full px-4 md:px-8 space-y-12 animate-pulse">
      <div className="flex flex-col md:flex-row items-center gap-12">
        <div className="w-full md:w-1/2 aspect-video bg-gray-300 rounded-2xl" />
        <div className="w-full md:w-1/2 space-y-6">
          <div className="h-6 w-32 bg-gray-300 rounded" />
          <div className="h-12 w-3/4 bg-gray-300 rounded" />
          <div className="space-y-3">
            <div className="h-4 w-full bg-gray-200 rounded" />
            <div className="h-4 w-full bg-gray-200 rounded" />
            <div className="h-4 w-2/3 bg-gray-200 rounded" />
          </div>
          <div className="h-10 w-40 bg-gray-300 rounded-full" />
        </div>
      </div>
    </div>
  </div>
);

const LazySection = ({ children, minHeight, rootMargin = "200px" }) => {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    if (!("IntersectionObserver" in window)) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin }, // Start loading 200px before the section enters the viewport
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [rootMargin]);

  return (
    <div ref={sectionRef} style={{ minHeight: isInView ? "auto" : minHeight }}>
      {isInView ? (
        <Suspense fallback={<SectionSkeleton minHeight={minHeight} />}>
          {children}
        </Suspense>
      ) : (
        <SectionSkeleton minHeight={minHeight} />
      )}
    </div>
  );
};

const Home = () => {
  const metaDescription =
    "Reflection Futbol Academy (RFA) develops elite youth football and futbol players through technical and tactical training. Join our programs, camps, and community events today.";

  return (
    <div className="flex flex-col overflow-hidden">
      <Helmet>
        <title>Reflection Futbol Academy | Elite Youth Football Training</title>
        <meta name="description" content={metaDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://reflectionfutbolacademy.com" />

        {/* Social SEO (Open Graph) */}
        <meta
          property="og:title"
          content="Reflection Futbol Academy | Elite Youth Football Training in OKC"
        />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:image"
          content="https://reflectionfutbolacademy.com/assets/rfa-logo.webp"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Reflection Futbol Academy | Elite Youth Football Training",
            description: metaDescription,
            url: "https://reflectionfutbolacademy.com",
          })}
        </script>
      </Helmet>
      <h1 className="sr-only">
        Reflection Futbol Academy - Elite Youth Football Training in Oklahoma
        City
      </h1>

      {/* Hero loads immediately as it's the "Main Page" the user sees first */}
      <Hero />

      {/* Sections restored for mobile view */}
      <LazySection minHeight="600px">
        <Features />
      </LazySection>

      <LazySection minHeight="800px">
        <About />
      </LazySection>

      <LazySection minHeight="600px">
        <Programs />
      </LazySection>

      <LazySection minHeight="500px">
        <Contact />
      </LazySection>
    </div>
  );
};

export default Home;
